import React, { useEffect, useState } from "react";
import axiosInstance from "../../services/api/axios";
import "react-toastify/dist/ReactToastify.css";
import { FormModal } from "../form";
import ImageCarousel from "../ImageCarousel";
import { normalizePropertyConfigMediaForUI } from "../../utils/media";

/**
 * Read-only modal showing a property configuration's areas, pricing and
 * media (gallery carousel + detailed floor plans). The pre-refactor file
 * had dead `isEditing` / `newImages` state from an abandoned edit path;
 * removed here since no UI ever triggered it.
 */
const ConfigModal = ({ selectedConfig, onRequestClose, isOpen }) => {
    const [loading, setLoading] = useState(false);
    const [mediaImages, setMediaImages] = useState([]);
    const [detailedFloorPlansMedia, setDetailedFloorPlansMedia] = useState([]);

    useEffect(() => {
        if (!isOpen || !selectedConfig?.propertyConfigId) return;
        const fetchImages = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get("/property/config/media", {
                    params: { property_config_id: selectedConfig.propertyConfigId },
                });
                const { gallery, detailedFloorPlans } = normalizePropertyConfigMediaForUI(response);
                setMediaImages(gallery || []);
                setDetailedFloorPlansMedia(detailedFloorPlans || []);
            } catch (error) {
                console.error("Error fetching media images:", error);
                setMediaImages([]);
                setDetailedFloorPlansMedia([]);
            } finally {
                setLoading(false);
            }
        };
        fetchImages();
    }, [isOpen, selectedConfig]);

    const isShop = selectedConfig?.propertyType === "Shop";

    const InfoRow = ({ label, value }) => (
        <div className="p-1">
            <p className="flex justify-between items-center text-sm">
                <span className="font-medium text-gray-700">{label}:</span>
                <span className="text-gray-900">{value}</span>
            </p>
        </div>
    );

    return (
        <FormModal
            open={isOpen}
            onClose={onRequestClose}
            title={selectedConfig?.configName || "Configuration"}
            size="xl"
        >
            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <div className="text-base font-medium text-flamingo-600">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-flamingo-600 mx-auto mb-3" />
                        Loading...
                    </div>
                </div>
            ) : selectedConfig ? (
                <div>
                    <div className="relative mb-6 overflow-hidden">
                        <ImageCarousel images={mediaImages} />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-lg">
                        <div className="space-y-1">
                            <InfoRow label="Type" value={selectedConfig.propertyType} />
                            {!isShop && (
                                <InfoRow
                                    label="Dry Balcony Area"
                                    value={`${selectedConfig.dryBalconyAreaSqmt} sq mt / ${selectedConfig.dryBalconyAreaSqft} sq ft`}
                                />
                            )}
                            {!isShop && <InfoRow label="Size" value={`${selectedConfig.sizeBhk} BHK`} />}
                            <InfoRow
                                label="Carpet Area"
                                value={`${selectedConfig.carpetAreaSqmt} sq mt / ${selectedConfig.carpetAreaSqft} sq ft`}
                            />
                        </div>
                        <div className="space-y-1">
                            <InfoRow
                                label="Super Built-up Area"
                                value={`${selectedConfig.superBuiltupAreaSqmt} sq mt / ${selectedConfig.superBuiltupAreaSqft} sq ft`}
                            />
                            {!isShop && (
                                <InfoRow
                                    label="Balcony Area"
                                    value={`${selectedConfig.balconyAreaSqmt} sq mt / ${selectedConfig.balconyAreaSqft} sq ft`}
                                />
                            )}
                            <InfoRow
                                label="Base Price"
                                value={`₹${Number(selectedConfig.basePrice || 0).toLocaleString()}`}
                            />
                        </div>
                    </div>

                    {detailedFloorPlansMedia.length > 0 && (
                        <div className="mt-8 pt-6 border-t border-gray-200">
                            <h4 className="text-md font-semibold text-gray-900 mb-4">
                                Detailed floor plans
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {detailedFloorPlansMedia.map((floorPlan, index) => (
                                    <div
                                        key={floorPlan.url || `floor-${index}`}
                                        className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50 hover:shadow-md transition-shadow"
                                    >
                                        {floorPlan.contentType === "application/pdf" ? (
                                            <a
                                                href={floorPlan.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex p-4 h-36 items-center justify-center"
                                            >
                                                <div className="text-center">
                                                    <svg
                                                        className="w-12 h-12 mx-auto mb-2 text-red-600"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                    <span className="text-xs text-gray-600">PDF</span>
                                                </div>
                                            </a>
                                        ) : (
                                            <a
                                                href={floorPlan.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block"
                                            >
                                                <img
                                                    src={floorPlan.url}
                                                    alt={`Floor plan ${index + 1}`}
                                                    className="w-full h-36 object-cover cursor-pointer"
                                                />
                                            </a>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="p-6 text-center text-gray-500">
                    <p>No configurations available for this project.</p>
                </div>
            )}
        </FormModal>
    );
};

export default ConfigModal;
